import { Location } from 'history'
import { match, matchPath, RouteProps } from 'react-router'
import {
  actionUnavailableRouteProps,
  addExpenseRouteProps,
  bulkUploadExpenseLabelsRouteProps,
  CancelSubscriptionMatchParams,
  cancelSubscriptionRouteProps,
  changePasswordRouteProps,
  closeAccountRouteProps,
  closeBillPayCardRouteProps,
  closeVirtualCardRouteProps,
  createAutomationRuleRouteProps,
  createBillPayRouteProps,
  createBudgetRouteProps,
  createVirtualCardRouteProps,
  cspUserDetailRouteProps,
  DeactivateBudgetMatchParams,
  deactivateBudgetRouteProps,
  downloadTransactionsRouteProps,
  expensesTransactionsRouteProps,
  featureFlaggerRouteProps,
  helpRouteProps,
  invitePeopleRouteProps,
  ManageOrgIntegrationMatchParams,
  manageOrgIntegrationRouteProps,
  manageOrgInviteRouteProps,
  manageTrustedDevicesRouteProps,
  peopleCancelOrgInviteRouteProps,
  peopleInvitedToOrgDetailRouteProps,
  peopleInvitedToOrgRouteProps,
  peopleMembersDetailRouteProps,
  peopleMembersRouteProps,
  registerAccountRouteProps,
  registerOrganizationRouteProps,
  removeUserFromOrganizationRouteProps,
  requestBudgetRouteProps,
  requestVirtualCardRouteProps,
  screenVirtualCardRouteProps,
  searchRouteProps,
  setOrgExpenseReportCycleRouteProps,
  ShareBillPayVirtualCardMatchParams,
  shareBillPayVirtualCardRouteProps,
  transactionDetailRouteProps,
  updateAutomationRuleRouteProps,
  updateBillPayRouteProps,
  updateBudgetRequestRouteProps,
  updateBudgetRouteProps,
  updateBulkTransactionsRouteProps,
  updateExpenseRouteProps,
  updateProfilePhotoRouteProps,
  updateVirtualCardRouteProps,
  upgradeSubscriptionRouteProps,
  userScreeningInfoRouteProps,
} from 'src/navigation/routeProps'
import {
  budgetOverviewUrl,
  homeUrl,
  organizationInfoUrl,
  organizationIntegrationsUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
  profileInfoUrl,
  profileSecurityUrl,
  signinUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'
import { LocationState } from 'src/redux/createRootReducer'

interface RoutePropsWithBackgroundLocation {
  routeProps: RouteProps
  exceptionRouteProps?: RouteProps[]
  calculateDefaultBackgroundLocation: (
    location: Location<LocationState>,
  ) => Location<LocationState>
}

export const authedPopupRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: screenVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: deactivateBudgetRouteProps,
      calculateDefaultBackgroundLocation: (
        location: Location<LocationState>,
      ): Location<LocationState> => {
        const routeMatch = matchPath<DeactivateBudgetMatchParams>(
          location.pathname,
          deactivateBudgetRouteProps,
        ) as match<DeactivateBudgetMatchParams>
        return {
          pathname: budgetOverviewUrl(routeMatch.params.id),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: registerAccountRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateProfilePhotoRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: profileInfoUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: upgradeSubscriptionRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: userScreeningInfoRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: profileInfoUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: manageOrgIntegrationRouteProps,
      calculateDefaultBackgroundLocation: (
        location: Location<LocationState>,
      ): Location<LocationState> => {
        const routeMatch = matchPath<ManageOrgIntegrationMatchParams>(
          location.pathname,
          manageOrgIntegrationRouteProps,
        ) as match<ManageOrgIntegrationMatchParams>
        return {
          pathname: organizationIntegrationsUrl(routeMatch.params.id),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: actionUnavailableRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: downloadTransactionsRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: removeUserFromOrganizationRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: peopleMembersUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: closeVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: closeBillPayCardRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: closeAccountRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: setOrgExpenseReportCycleRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: shareBillPayVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (
        location,
      ): Location<LocationState> => {
        const routeMatch = matchPath<ShareBillPayVirtualCardMatchParams>(
          location.pathname,
          shareBillPayVirtualCardRouteProps,
        )

        return {
          pathname: routeMatch?.params?.id
            ? virtualCardDetailUrl(routeMatch.params.id)
            : homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: cancelSubscriptionRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => {
        const routeMatch = matchPath<CancelSubscriptionMatchParams>(
          location.pathname,
          cancelSubscriptionRouteProps,
        ) as match<ManageOrgIntegrationMatchParams>

        return {
          pathname: organizationInfoUrl(routeMatch?.params?.id),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
  ]

export const authedLeftFlyoutRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: helpRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: searchRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: cspUserDetailRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
  ]

export const authedRightFlyoutRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: createVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: requestVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: createBudgetRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: requestBudgetRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBudgetRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBudgetRequestRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: transactionDetailRouteProps,
      exceptionRouteProps: [expensesTransactionsRouteProps],
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: invitePeopleRouteProps,
      exceptionRouteProps: [
        peopleMembersRouteProps,
        peopleInvitedToOrgRouteProps,
      ],
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: peopleMembersUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: peopleMembersDetailRouteProps,
      exceptionRouteProps: [peopleMembersRouteProps],
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: peopleMembersUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: peopleInvitedToOrgDetailRouteProps,
      exceptionRouteProps: [peopleInvitedToOrgRouteProps],
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: peopleInvitedToOrgUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: peopleCancelOrgInviteRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: peopleInvitedToOrgUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: registerOrganizationRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: manageOrgInviteRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: featureFlaggerRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: changePasswordRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: profileSecurityUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: manageTrustedDevicesRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: profileSecurityUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: addExpenseRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateExpenseRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBulkTransactionsRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: createBillPayRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBillPayRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: createAutomationRuleRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateAutomationRuleRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: bulkUploadExpenseLabelsRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
  ]

export const unauthedRightFlyoutRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: featureFlaggerRouteProps,
      calculateDefaultBackgroundLocation: (): Location<LocationState> => ({
        pathname: signinUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
  ]

export const authedFlyoutRoutePropsWithBackgroundLocations = [
  ...authedPopupRoutePropsWithBackgroundLocations,
  ...authedLeftFlyoutRoutePropsWithBackgroundLocations,
  ...authedRightFlyoutRoutePropsWithBackgroundLocations,
]
